import {
  chartHeight,
  chartInnerRadius,
  chartOuterRadius,
  chartWidth,
  countUpTime,
  DeviceIntegrationCard,
  fadeTimeout,
  saturation,
} from '../Styles/DeviceIntegrationStyles';
import { ServiceStatusStatistic } from '@ekkogmbh/apisdk';
import React, { useMemo } from 'react';
import { Color, colors, Stack, Typography } from '@mui/material';
import { PieChart } from 'recharts';
import { Pie } from 'recharts';
import { Cell } from 'recharts';
import CountUp from 'react-countup';
import { CenteredColumn } from 'src/Common/Components/Stacks';

export const statusToColor = (status: string): Color | undefined => {
  switch (status) {
    case 'offline':
    case 'bad':
    case 'disconnected':
      return colors.red;
    case 'online':
    case 'good':
    case 'succesful': // anticipating typo
    case 'successful':
    case 'connected':
      return colors.green;
    case 'excellent':
      return colors.lightGreen;
    case 'unknown':
      return colors.grey;
    default:
      return undefined;
  }
};

const keyToLabel = (key: string): string => key.split(/(?=[A-Z])/).join(' ');
interface Props {
  statistic: ServiceStatusStatistic;
  stripped?: boolean;
}

export const StatisticElement = ({ statistic, stripped = false }: Props): React.JSX.Element => {
  const data = useMemo(
    () =>
      Object.keys(statistic.data).map((name) => ({
        name,
        value: statistic.data[name],
        color: statusToColor(name) ?? colors.orange,
      })),
    [statistic],
  );

  const captions = useMemo(() => {
    const parts = data
      .flatMap(({ name }, i) => [
        <Typography key={'cap' + i} variant={'caption'}>
          {name}
        </Typography>,
        <Typography key={'sep' + i} variant={'caption'}>
          /
        </Typography>,
      ])
      .slice(0, -1);

    return (
      <Stack direction={'row'} spacing={1} width={'100%'} justifyContent={'center'}>
        {parts}
      </Stack>
    );
  }, [data]);

  const counts = useMemo(() => {
    const parts = data
      .flatMap(({ color, value }, i) => [
        <Typography key={'count' + i} color={color[saturation]} fontSize={24} fontWeight={700}>
          <CountUp start={0} end={value} duration={countUpTime / 1000} delay={fadeTimeout / 1000} />
        </Typography>,
        <Typography key={'sep' + i} fontSize={24}>
          /
        </Typography>,
      ])
      .slice(0, -1);

    return (
      <Stack direction={'row'} spacing={1} width={'100%'} justifyContent={'center'} sx={{ userSelect: 'none' }}>
        {parts}
      </Stack>
    );
  }, [data]);

  const title = useMemo(() => {
    const splitName = keyToLabel(statistic.name);
    return splitName.charAt(0).toUpperCase() + splitName.slice(1);
  }, [statistic.name]);

  return (
    <DeviceIntegrationCard title={title} caption={captions} stripped={stripped}>
      <CenteredColumn>
        <PieChart
          width={chartWidth}
          height={chartHeight}
          sx={{
            transform: 'rotate(-90deg)',
          }}
        >
          <Pie
            data={data}
            innerRadius={chartInnerRadius}
            outerRadius={chartOuterRadius}
            paddingAngle={0}
            dataKey={'value'}
          >
            {data.map(({ color }, index: number) => (
              <Cell key={index} fill={color[saturation]} />
            ))}
          </Pie>
        </PieChart>
      </CenteredColumn>
      {counts}
    </DeviceIntegrationCard>
  );
};
